// hairLoadProgress — tiny pub/sub store for the GSS hair load overlay.
//
// The splat hair takes ~40s to initialise (ONNX-on-wasm session compiles).
// HairRenderer only emits 4 coarse step callbacks and the big cost (the ONNX
// compile) is one opaque step, so a true byte-% isn't available. We show a
// time-estimated bar that creeps toward ~92% over EST_MS, floored by real
// stage completions, and snaps to 100% on ready. Honest "estimated" progress
// that always moves — better than a frozen bar.
//
// HairSplat drives it (start/step/finish/fail); AvatarLoadOverlay renders it as
// the tail (hair-init) phase of the unified boot progress bar.

import { assetLoadProgress } from './assetLoadProgress.js'

let _state = { active: false, fraction: 0, label: '', expected: false }
let _listeners = []
let _timer = null
let _expectTimer = null
let _start = 0
let _floor = 0
let _gen = 0   // generation token — guards against StrictMode double-mount churn

const EST_MS = 42000
const CREEP_CAP = 0.92
// Floors are deliberately LOW for the early steps: hairMesh/hairOnnx fire
// before the expensive work (ONNX session compiles + 149MB .ply), so the
// time-based creep — not a step floor — should drive the bar through that
// opaque middle. hairProbe/hairShade fire near the end and bump it up.
const STEP_FLOOR = { hairMesh: 0.12, hairOnnx: 0.22, hairProbe: 0.80, hairShade: 0.95 }
const STEP_LABEL = {
  hairMesh: 'Loading hair model…',
  hairOnnx: 'Building shaders…',
  hairProbe: 'Sampling lighting…',
  hairShade: 'Finalizing…',
}

function notify() { for (const fn of _listeners) fn(_state) }

// Release the reserved hair tail only if hair never actually starts. The grace
// must NOT fire while the boot DOWNLOADS are still in flight: on a slow load
// they routinely exceed the grace, and start() only fires AFTER downloads
// finish (HairSplat mounts once loadOgsContentPacks resolves) — releasing then
// would drop the reservation mid-download and flash the bar to 100% before hair
// init reappears at 75%. So re-arm while downloads are active; only release once
// they've settled and start() still hasn't fired (the genuine "hair.gsh shipped
// but the blueprint never wires it" case the guard exists for).
function armExpectRelease() {
  clearTimeout(_expectTimer)
  _expectTimer = setTimeout(() => {
    if (_state.active || !_state.expected) return          // hair started, or already released
    if (assetLoadProgress.get().active) { armExpectRelease(); return } // downloads still running
    _state = { ..._state, expected: false }; notify()
  }, 6000)
}

function tick() {
  const elapsed = Date.now() - _start
  const timeBased = Math.min(CREEP_CAP, elapsed / EST_MS)
  _state = { ..._state, fraction: Math.max(timeBased, _floor) }
  notify()
}

export const hairLoadProgress = {
  subscribe(fn) {
    _listeners.push(fn)
    fn(_state)
    return () => { _listeners = _listeners.filter(l => l !== fn) }
  },
  get() { return _state },

  // "Hair is coming" — set the moment we know this avatar WILL load hair
  // (its OGS bundle ships a hair.gsh), BEFORE the actual ~40s init start()s.
  // AvatarLoadOverlay reserves its tail (the [75,100%] span) only while hair is
  // expected-or-active; a no-hair avatar never calls this, so its download bar
  // spans the full [0,100%] and reaches 100% instead of stalling at 75%.
  //
  // Safety: a bundle can ship a hair.gsh that the blueprint never connects (so
  // start() never fires). armExpectRelease() auto-releases the expectation once
  // downloads settle without a hair start, so the reserved tail can't pin the
  // boot bar forever — without releasing mid-download (see armExpectRelease).
  expect() {
    if (!_state.expected) { _state = { ..._state, expected: true }; notify() }
    armExpectRelease()
  },

  // Returns a token; pass it to finish()/fail() so a superseded attempt
  // (StrictMode remount, retry after a transient failure) can't clear the bar
  // for the attempt that's actually still running.
  start(label = 'Loading hair…') {
    _gen += 1
    _start = Date.now()
    _floor = 0.03
    clearTimeout(_expectTimer)
    _state = { active: true, fraction: 0.03, label, expected: true }
    notify()
    clearInterval(_timer)
    _timer = setInterval(tick, 250)
    return _gen
  },

  step(name) {
    if (STEP_FLOOR[name] != null) _floor = Math.max(_floor, STEP_FLOOR[name])
    if (STEP_LABEL[name]) _state = { ..._state, label: STEP_LABEL[name] }
    notify()
  },

  finish(token) {
    if (token != null && token !== _gen) return
    clearInterval(_timer); _timer = null
    clearTimeout(_expectTimer)
    _state = { active: true, fraction: 1, label: 'Ready', expected: true }
    notify()
    setTimeout(() => {
      if (token != null && token !== _gen) return
      _state = { active: false, fraction: 0, label: '', expected: false }; notify()
    }, 700)
  },

  fail(token) {
    if (token != null && token !== _gen) return
    clearInterval(_timer); _timer = null
    clearTimeout(_expectTimer)
    _state = { active: false, fraction: 0, label: '', expected: false }
    notify()
  },
}

if (typeof window !== 'undefined') window.__HAIR_PROGRESS__ = hairLoadProgress
